import { getFunctions, httpsCallable } from "firebase/functions";
import app from "../../firebaseConfig";

// ---------------------------------------------------------------------------
// Pro API client.
// Development: calls go through the CRA dev proxy (/proapi -> local DrafterEye,
// see src/setupProxy.js) so the section can be built and demoed without
// deploying anything.
// Production: calls go through the `proApi` Cloud Function, which checks the
// Firestore `proAccess` flag server-side and forwards to DrafterEye with the
// Cloudflare service token (never exposed to the browser).
// ---------------------------------------------------------------------------

const DEV_DIRECT = process.env.NODE_ENV === "development";

const functions = getFunctions(app);
const proApiCall = httpsCallable<
  { path: string; method?: string; payload?: unknown },
  unknown
>(functions, "proApi");

async function callDev(
  path: string,
  method: "GET" | "POST",
  payload?: unknown,
): Promise<unknown> {
  const res = await fetch(`/proapi${path}`, {
    method,
    headers: payload ? { "Content-Type": "application/json" } : undefined,
    body: payload ? JSON.stringify(payload) : undefined,
  });
  const data = await res.json();
  if (!res.ok) {
    throw new Error(
      (data as { error?: string })?.error || `HTTP ${res.status}`,
    );
  }
  return data;
}

async function call(
  path: string,
  method: "GET" | "POST",
  payload?: unknown,
): Promise<unknown> {
  if (DEV_DIRECT) return callDev(path, method, payload);
  const res = await proApiCall({ path, method, payload });
  return res.data;
}

// ---- Typed endpoints ------------------------------------------------------

export type Role = "Top" | "Jungle" | "Mid" | "Bot" | "Sup";
export const ROLES: Role[] = ["Top", "Jungle", "Mid", "Bot", "Sup"];

export type TierListItem = {
  // self items
  champ?: string;
  role?: Role;
  // pair items (syn / opp_same / opp_diff)
  champ_a?: string;
  role_a?: Role;
  champ_b?: string;
  role_b?: Role;
  gain: number;
  n: number;
  n_recent: number;
  n_patch: number;
  n_window?: number;
  note: number;
  pct: number | null;
  tier: string;
};

export type InteractionsIndex = {
  champions: string[];
  patches: string[];
  meta: Record<string, unknown>;
};

export type ChampionReportItem = {
  type: string;
  role: Role;
  partner: string | null;
  partner_role: Role | null;
  gain: number;
  n: number;
  n_recent: number;
  n_patch: number;
  n_window: number;
  note: number;
  pct: number | null;
};

export type ChampionGames = {
  total_all: number;
  roles_all: Partial<Record<Role, number>>;
  total_window: number;
  roles_window: Partial<Record<Role, number>>;
  weeks: number;
};

export type ChampionReport = {
  champion: string;
  roles: Role[];
  self: ChampionReportItem[];
  synergies: ChampionReportItem[];
  matchups_direct: ChampionReportItem[];
  matchups_indirect: ChampionReportItem[];
  games: ChampionGames;
  patch_max: string | null;
  current_patch: string | null;
};

export type BreakDetail = {
  target: string;
  target_role: Role;
  pct: number | null;
  n: number;
  kind: "direct" | "indirect" | "synergy";
};

export type BreakItem = {
  champ: string;
  role: Role;
  pct: number | null;
  coverage: number;
  self_pct: number | null;
  note: number;
  n_recent: number;
  n_patch: number;
  details: BreakDetail[];
};

export type SuggestCounter = {
  champion: string;
  role: Role;
  delta: number;
} | null;

export type SuggestItem = {
  champion: string;
  value: number;
  value_safe: number;
  counter: SuggestCounter;
  why: string;
};

export type SuggestPickResponse = {
  slot: number;
  role: Role;
  team: "blue" | "red";
  model: { id: string | null; name: string | null };
  items: SuggestItem[];
};

export type SuggestBanItem = SuggestItem & { role: Role; slot: number };

export type SuggestBanResponse = {
  team: "blue" | "red";
  opponent: "blue" | "red";
  items: SuggestBanItem[];
};

export type DraftScoreResponse = {
  complete: boolean;
  filled: number;
  dscore: number | null;
  p_blue: number | null;
  p_red: number | null;
};

export function getInteractionsIndex(): Promise<InteractionsIndex> {
  return call("/api/interactions", "GET") as Promise<InteractionsIndex>;
}

export function getTierList(params: {
  kind: "self" | "syn" | "opp_same" | "opp_diff";
  patch?: string;
  role_a?: string;
  role_b?: string;
  min_recent?: number;
  min_n?: number;
  weeks?: number;
}): Promise<{ items: TierListItem[]; use_patch: boolean }> {
  const q = new URLSearchParams();
  q.set("kind", params.kind);
  if (params.weeks !== undefined) q.set("weeks", String(params.weeks));
  if (params.patch) q.set("patch", params.patch);
  if (params.role_a) q.set("role_a", params.role_a);
  if (params.role_b) q.set("role_b", params.role_b);
  if (params.min_recent !== undefined)
    q.set("min_recent", String(params.min_recent));
  if (params.min_n !== undefined) q.set("min_n", String(params.min_n));
  return call(`/api/interactions/tierlist?${q.toString()}`, "GET") as Promise<{
    items: TierListItem[];
    use_patch: boolean;
  }>;
}

export function getChampionReport(
  champion: string,
  params: {
    patch?: string;
    role?: string;
    min_n?: number;
    weeks?: number;
  } = {},
): Promise<ChampionReport> {
  const q = new URLSearchParams();
  if (params.patch) q.set("patch", params.patch);
  if (params.role) q.set("role", params.role);
  if (params.min_n !== undefined) q.set("min_n", String(params.min_n));
  if (params.weeks !== undefined) q.set("weeks", String(params.weeks));
  const qs = q.toString();
  return call(
    `/api/interactions/${encodeURIComponent(champion)}${qs ? `?${qs}` : ""}`,
    "GET",
  ) as Promise<ChampionReport>;
}

export function breakSelection(payload: {
  kind: "opp" | "syn";
  selected: [string, string][];
  patch?: string;
  role?: string;
}): Promise<{ items: BreakItem[] }> {
  return call("/api/interactions/break", "POST", payload) as Promise<{
    items: BreakItem[];
  }>;
}

export function suggestPick(payload: {
  blue: string[];
  red: string[];
  slot: number;
  bans?: string[];
  top_n?: number;
  meta?: boolean;
  meta_days?: number;
  meta_min_picks?: number;
}): Promise<SuggestPickResponse> {
  return call(
    "/api/suggest-pick",
    "POST",
    payload,
  ) as Promise<SuggestPickResponse>;
}

export type SuggestAnyResponse = {
  team: "blue" | "red";
  model: { id: string | null; name: string | null };
  items: SuggestBanItem[];
};

export function suggestPickAny(payload: {
  blue: string[];
  red: string[];
  team: "blue" | "red";
  bans?: string[];
  top_n?: number;
  meta?: boolean;
  meta_days?: number;
  meta_min_picks?: number;
}): Promise<SuggestAnyResponse> {
  return call(
    "/api/suggest-pick-any",
    "POST",
    payload,
  ) as Promise<SuggestAnyResponse>;
}

export function suggestBan(payload: {
  blue: string[];
  red: string[];
  team: "blue" | "red";
  bans?: string[];
  top_n?: number;
  meta?: boolean;
  meta_days?: number;
  meta_min_picks?: number;
}): Promise<SuggestBanResponse> {
  return call(
    "/api/suggest-ban",
    "POST",
    payload,
  ) as Promise<SuggestBanResponse>;
}

export function draftScore(payload: {
  blue: string[];
  red: string[];
}): Promise<DraftScoreResponse> {
  return call(
    "/api/draft-score",
    "POST",
    payload,
  ) as Promise<DraftScoreResponse>;
}
